import { fragmentRegistry } from '@redwoodjs/web/apollo'

import { AddressFragment } from 'src/lib/fragments/addresses'
import { AIChatFallbackContactFragment } from 'src/lib/fragments/aiChatFallbackContacts'
import {
  AIAssistantChatFragment,
  AIChatBaseFragment,
  AIChatCitationFragment,
  AIChatResponseEventFragment,
  AIAssistantChatAssociatedAIChatbotFAQFragment,
} from 'src/lib/fragments/aiChats'
import {
  AutoCheckExecutionFragment,
  AutoCheckFeedbackItemBaseFragment,
  AutoCheckFeedbackItemFragment,
  AutoCheckFeedbackItemSourceFragment,
  AutoCheckFeedbackUpdateFragment,
} from 'src/lib/fragments/autoCheck'
import { AutomationTemplateFragment } from 'src/lib/fragments/automationTemplates'
import { CodeBookItemFragment, CodeBookItemOptionFragment } from 'src/lib/fragments/codeBookItems'
import { CodeBookFragment } from 'src/lib/fragments/codeBooks'
import { ConfigConnectionFragment } from 'src/lib/fragments/configConnections'
import {
  SearchEntityVersionFragment,
  EntityVersionWithFieldsFragment,
  SearchEntityVersionsByLocationFragment,
} from 'src/lib/fragments/entityVersions'
import { EnumerationFragment } from 'src/lib/fragments/enumerations'
import { FeeScheduleFragment } from 'src/lib/fragments/feeSchedules'
import { RecipientContactFieldFragment } from 'src/lib/fragments/fields'
import { FileOnRecordDeprecatedFragment } from 'src/lib/fragments/fileOnRecord'
import { GeneratedFileFragment } from 'src/lib/fragments/files'
import { FormulaFragment, FormulaOptionFragment } from 'src/lib/fragments/formulas'
import { GeneralLedgerNumberFragment } from 'src/lib/fragments/generalLedgerNumbers'
import {
  InspectionAvailabilityDayFragment,
  InspectionAvailabilitySlotFragment,
} from 'src/lib/fragments/inspectionAvailability'
import { InspectionAvailabilityWindowDataFragment } from 'src/lib/fragments/inspectionAvailabilityWindows'
import { LateFeeConfigurationFragment } from 'src/lib/fragments/lateFeeConfigurations'
import { LetterheadDetailFragment } from 'src/lib/fragments/letterheads'
import { MeetingResultFragment } from 'src/lib/fragments/meetingResults'
import { MeetingOptionFragment } from 'src/lib/fragments/meetings'
import { OrganizationHolidayDetailFragment } from 'src/lib/fragments/organizationHolidays'
import { OrganizationHomePageFragment } from 'src/lib/fragments/organizationHomePage'
import { PersonalInspectionAvailabilityWindowDataFragment } from 'src/lib/fragments/personalInspectionAvailabilityWindows'
import { PlanReviewFieldGroupFragment, PlanReviewFileFragment } from 'src/lib/fragments/planReview'
import { RecordAccessFragment } from 'src/lib/fragments/recordAccess'
import { RecordEmailFileFragment } from 'src/lib/fragments/recordEmailFiles'
import { RecordEmailFragment } from 'src/lib/fragments/recordEmails'
import { RecordOnMeetingFragment } from 'src/lib/fragments/recordOnMeetings'
import {
  AlertsForRecordFragment,
  RecordFragment,
  RecordIdentifierFragment,
  RecordIssuedIdentifierFragment,
  RecordOptionFragment,
  RecordUpdatedRecordTemplateHistoriesFragment,
  RelatedRecordFragment,
} from 'src/lib/fragments/records'
import {
  OverridableRecordSettingsFragment,
  OverridableRecordSettingSourcesFragment,
  RecordSettingsFragment,
  RecordSettingSourceFragment,
} from 'src/lib/fragments/recordSettings'
import {
  RecordTaskInspectionAttemptDetailsFragment,
  RecordTaskInspectionAttemptFragment,
  RecordTaskInspectionAttemptOverviewFragment,
} from 'src/lib/fragments/recordTaskInspectionAttempts'
import { RecordTaskInspectionResultFragment } from 'src/lib/fragments/recordTaskInspectionResults'
import { RecordTaskChecklistItemFragment } from 'src/lib/fragments/recordTasks'
import { RecordTaskTemplateGroupRequestedRecordFragment } from 'src/lib/fragments/recordTaskTemplateGroupRequestedRecordConfig'
import { RecordTaskTemplateGroupOptionFragment } from 'src/lib/fragments/recordTaskTemplateGroups'
import {
  RecordTemplateOptionFragment,
  RecordTemplatePickFragment,
} from 'src/lib/fragments/recordTemplates'
import { RecordTypeApplicationGroupFragment } from 'src/lib/fragments/recordTypes'
import { ScrapeFragment } from 'src/lib/fragments/scrape'
import { ScrapeTemplateFragment } from 'src/lib/fragments/scrapeTemplates'
import {
  SentEmailFragment,
  SentEmailRecipientFragment,
  SentEmailDetailedFragment,
} from 'src/lib/fragments/sentEmails'
import {
  SettlementFragment,
  SettlementFragmentOverview,
  SettlementPaymentTransferFragment,
  SettlementPaymentTransferRecordTaskFeeFragment,
} from 'src/lib/fragments/settlements'
import { StampTemplateFragment } from 'src/lib/fragments/stampTemplates'
import { UserCompanyInvitationFragment } from 'src/lib/fragments/userCompanyInvitations'
import { UserOnCompanyFragment } from 'src/lib/fragments/userOnCompanies'
import {
  ViewDashboardFragment,
  ViewDashboardOverviewFragment,
} from 'src/lib/fragments/viewDashboards'
import {
  ViewDataElementFragment,
  ViewFragment,
  ViewColumnFragment,
  ViewFilterFragment,
  ViewFilterDateFragment,
  ViewSortFragment,
  ViewColumnTemplateFragment,
  ViewFilterOptionFragment,
  FieldTemplateForViewFragment,
  ViewColumnUsedInViewFragment,
  EntityForViewFragment,
  ViewDataFragment,
  ViewFilterNumberFragment,
  ViewFilterMembershipFragment,
} from 'src/lib/fragments/views'
import {
  ViolationCodeFragment,
  ViolationCommentFragment,
  ViolationFileFragment,
  ViolationFragment,
  ViolationInspectionFragment,
  ViolationMailerFragment,
  ViolationMailerTitleFragment,
  ViolationTypeFragment,
} from 'src/lib/fragments/violations'
import {
  ValueForFunctionFragment,
  WidgetDataBucketFragment,
  WidgetDataGroupByFragment,
  WidgetDataGroupFragment,
} from 'src/lib/fragments/widgets'
import {
  RecordTaskBaseFragment,
  RecordTaskGroupBaseFragment,
  WorkflowFragment,
  WorkflowOptionFragment,
  WorkflowRecordTaskFragment,
  WorkflowRecordTaskGroupFragment,
} from 'src/lib/fragments/workflows'
import { WorkflowTemplateOptionFragment } from 'src/lib/fragments/workflowTemplates'

import {
  AIChatSessionFragment,
  StaffAIChatSessionPreviewFragment,
  StaffAIChatSessionWithMessagesFragment,
} from './aiChatSessions'
import { CalendarEventFragment } from './calendarEvents'
import { EntityLayerFragment } from './entityLayers'
import { AuthorizedExternalCalendarFragment, ExternalCalendarFragment } from './externalCalendars'
import {
  BackwardsCompatibleAddressFragment,
  BackwardsCompatibleLocationFieldFragment,
} from './gis-migration'
import { RecordPlansSetReviewResultConfigFragment } from './recordPlanReviewResults'
import { UserRequestToJoinCompanyFragment } from './userRequestToJoinCompanies'

const UserDataFragment = gql`
  fragment UserDataFragment on User {
    canViewForeignValues
    cellPhone
    clerkUserId
    createdAt
    emailAddress
    firstName
    group
    hasCodeV1Access
    id
    imageUrl
    isAIStaffMember
    isCodeViolationsEmployeeReference
    isGovWellStaff
    isInspector
    isLandingPageEmployeeReference
    isPermittingEmployeeReference
    isPlanReviewer
    isScheduler
    lastName
    officePhone
    organizationId
    roles
    status
    title
    type
    uuid
    externalCalendar {
      ...ExternalCalendarFragment
    }
    hasOverridenOrgWideInspectionAvailabilityWindows
    personalInspectionAvailabilityWindows {
      ...PersonalInspectionAvailabilityWindowDataFragment
    }
  }
`

const UserNameFragment = gql`
  fragment UserNameFragment on User {
    firstName
    lastName
    emailAddress
  }
`

const UserDataFragmentWithCompany = gql`
  fragment UserDataFragmentWithCompany on User {
    ...UserDataFragment
    company {
      ...CompanyFragment
    }
    companies {
      ...CompanyFragment
    }
  }
`

const ViolationTypeHistoryFragmentNoUserData = gql`
  fragment ViolationTypeHistoryFragmentNoUserData on Violation {
    violationHistory {
      violationTypeOther
      createdAt
      violationTypes {
        id
        name
        description
      }
    }
    previousViolationHistory {
      violationTypeOther
      createdAt
      violationTypes {
        id
        name
        description
      }
    }
  }
`

const ViolationTypeHistoryFragment = gql`
  fragment ViolationTypeHistoryFragment on ViolationHistory {
    address
    violationTypeOther
    createdAt
    status
    violationTypes {
      id
      name
      description
    }
    createdByUser {
      ...UserDataFragment
    }
  }
`

const TaskTemplatesFragment = gql`
  fragment TaskTemplateFragment on TaskTemplate {
    id
    identifier
    status
  }
`

const FileFragment = gql`
  fragment FileFragment on File {
    name
    id
    downloadableName
    isAllowedOnRecordPlansSets
    pageProcessingFinishedAt
    path
    size
    type
    createdAt
    createdByUser {
      firstName
      lastName
    }
    importedAt
    url {
      signedUrl
    }
  }
`

const ThumbnailFileFragment = gql`
  fragment ThumbnailFileFragment on File {
    ...FileFragment
    thumbnailUrl {
      signedUrl
    }
  }
`

const FilePageFragment = gql`
  fragment FilePageFragment on FilePage {
    id
    fileId
    heightPx
    pageNumber
    renderStatus
    rotationDegrees
    signedThumbnailImageUrl
    widthPx
  }
`

const FileUploadCredentialsFragment = gql`
  fragment FileUploadCredentialsFragment on FileUploadCredentials {
    accessKeyId
    secretAccessKey
    sessionToken
    region
    bucket
    key
  }
`

const EntityVersionFragment = gql`
  fragment EntityVersionFragment on EntityVersion {
    id
    entity {
      id
      name
    }
  }
`

const FieldValueBaseFragment = gql`
  fragment FieldValueBaseFragment on FieldValueBase {
    id
    basedOnFieldId
    type
    checked
    date
    num
    str
    strs
    allowMultiple
    order
    parameters
    basedOnEntityFieldTemplate {
      id
    }
    codeBookItems {
      id
    }
    contact {
      id
      firstName
      lastName
      middleName
      email
      companyName
      phoneNumber
      faxNumber
      address
      title
      addressLine1
      addressLine2
      city
      state
      zip
    }
    record {
      id
      issuedIdentifier
      identifier
      submittedAt
      stringified
      recordTemplate {
        id
        name
      }
      applicantUser {
        id
        firstName
        lastName
      }
    }
    cslbContractorSnapshot {
      ...CSLBContractorSnapshotFragment
    }
    inspectionResult {
      ...RecordTaskInspectionResultFragment
    }
    locationSnapshot {
      ...LocationSnapshotFragment
    }
    backwardsCompatibleLocationInformation {
      ...BackwardsCompatibleLocationInformationFragment
    }
    file {
      ...FileFragment
    }
    files {
      ...FileFragment
    }
    recordCollections {
      id
      name
    }
    foreignValue {
      id
      type
    }
    entityVersions {
      ...SearchEntityVersionFragment
    }
    users {
      ...UserDataFragment
    }
  }
  fragment LocationSnapshotFragment on LocationSnapshot {
    id
    parcelDisplayName
    locationObjectId
    owner
  }
  fragment CSLBContractorSnapshotFragment on CSLBContractorSnapshot {
    address
    displayName
    expirationDate
    id
    issueDate
    licenseNumber
    phoneNumber
    status
  }
`

const FieldFragment = gql`
  fragment FieldFragment on Field {
    ...FieldValueBaseFragment
    label
    required
    tooltip
    foreignValueType
    userHasAccessToForeignValue
    order
    recordId
    basedOnFieldId
    areFilesPlanReviewed
    showInGeneratedDocuments
    isArchived
    recordTemplates {
      id
      name
    }
    entityLayers {
      ...EntityLayerFragment
    }
    childFieldGroupTemplate {
      id
    }
  }
`

const FillRecordTemplateApplyFormFieldFragment = gql`
  fragment FillRecordTemplateApplyFormFieldFragment on FillRecordTemplateApplyFormField {
    ...FieldValueBaseFragment
    childFieldGroups {
      ...FillRecordTemplateApplyFormFieldGroupFragment
    }
  }
  fragment FillRecordTemplateApplyFormFieldGroupFragment on FillRecordTemplateApplyFormFieldGroup {
    fields {
      ...FieldValueBaseFragment
    }
  }
`

/*
Ideally, this fragment would not exist, but some places like record field groups/fields
currently act as their own template and value without a clear way to separate the responsibilities.
(EX: some field values don't have a basedOnFieldId)

Use FieldValueDetailedFragment or FieldTemplateDetailedFragment if possible.
*/
const FieldDetailedFragment = gql`
  fragment FieldDetailedFragment on Field {
    ...FieldFragment
    childFieldGroups {
      ...FieldGroupFragment
    }
    childFieldGroupTemplate {
      ...FieldGroupFragment
    }
  }
`

const FieldValueDetailedFragment = gql`
  fragment FieldValueDetailedFragment on Field {
    ...FieldFragment
    childFieldGroups {
      ...FieldGroupFragment
    }
  }
`

const FieldTemplateDetailedFragment = gql`
  fragment FieldTemplateDetailedFragment on Field {
    ...FieldFragment
    childFieldGroupTemplate {
      ...FieldGroupFragment
    }
  }
`

const FieldTemplateListFragment = gql`
  fragment FieldTemplateListFragment on Field {
    id
    isArchived
    label
    required
    type
    fieldGroupTemplate {
      id
      isSharedFieldGroup
    }
  }
`

const FieldGroupFragment = gql`
  fragment FieldGroupFragment on FieldGroup {
    id
    isSharedFieldGroup
    isChildFieldGroupTemplate
    isStaffOnlyEdit
    createdAt
    name
    fields {
      ...FieldFragment
    }
  }
`

/*
Ideally, this fragment would not exist, but some places like record field groups/fields
currently act as their own template and value without a clear way to separate the responsibilities.
(EX: some field values don't have a basedOnFieldId)

Use FieldGroupValueDetailedFragment or FieldGroupTemplateDetailedFragment if possible.
*/
const FieldGroupDetailedFragment = gql`
  fragment FieldGroupDetailedFragment on FieldGroup {
    ...FieldGroupFragment
    fields {
      ...FieldDetailedFragment
    }
  }
`

const FieldGroupValueDetailedFragment = gql`
  fragment FieldGroupValueDetailedFragment on FieldGroup {
    ...FieldGroupFragment
    fields {
      ...FieldValueDetailedFragment
    }
  }
`

const FieldGroupTemplateDetailedFragment = gql`
  fragment FieldGroupTemplateDetailedFragment on FieldGroup {
    ...FieldGroupFragment
    fields {
      ...FieldTemplateDetailedFragment
    }
    applicationFields {
      ...FieldFragment
    }
  }
`

const RecordTemplateFieldGroupDetailedFragment = gql`
  fragment RecordTemplateFieldGroupDetailedFragment on RecordTemplateFieldGroup {
    id
    isStaffOnlyEdit
    fieldGroup {
      ...FieldGroupTemplateDetailedFragment
    }
  }
`

const RecordTemplateFieldGroupFragment = gql`
  fragment RecordTemplateFieldGroupFragment on RecordTemplateFieldGroup {
    id
    isStaffOnlyEdit
    fieldGroup {
      ...FieldGroupFragment
    }
  }
`

const FieldGroupFragmentWithBasedOnFieldIds = gql`
  fragment FieldGroupFragmentWithBasedOnFieldIds on FieldGroup {
    id
    createdAt
    fields {
      ...FieldFragment
      basedOnFieldId
      basedOnEntityFieldTemplate {
        id
      }
    }
  }
`

const FieldGroupTableFragment = gql`
  fragment FieldGroupTableFragment on FieldGroup {
    id
    createdAt
    name
    isArchived
    archivedAt
    archivedByUser {
      ...UserDataFragment
    }
    fields {
      ...FieldFragment
    }
    usedInRecordTemplates {
      id
      name
    }
  }
`

const FieldGroupOnRecordSettingsFragment = gql`
  fragment FieldGroupOnRecordSettingsFragment on FieldGroupOnRecordSettings {
    id
    createdAt
    type
    createdByUser {
      ...UserDataFragment
    }
    fieldGroup {
      ...FieldGroupTableFragment
    }
  }
`

const ViolationFieldsFragment = gql`
  fragment ViolationFields on Violation {
    id
    uuid
    address
    status
    identifier
    createdAt
    reportedAt
    submitterType
    backwardsCompatibleLocationInformation {
      ...BackwardsCompatibleLocationInformationFragment
    }
    ownerUser {
      ...UserDataFragment
    }
    createdByUser {
      ...UserDataFragment
    }
    ...ViolationTypeHistoryFragmentNoUserData
    violationMailers {
      id
    }
  }
`

const ContactFragment = gql`
  fragment ContactFragment on Contact {
    id
    firstName
    lastName
    middleName
    email
    companyName
    phoneNumber
    faxNumber
    address
    title
    addressLine1
    addressLine2
    city
    state
    zip
  }
`

const ViolationInspectionsFragment = gql`
  fragment ViolationInspectionFields on ViolationInspection {
    id
    scheduledFor
    loggedAt
    status
    createdByUser {
      ...UserDataFragment
    }
    violation {
      id
      identifier
      address
      status
      createdAt
      backwardsCompatibleLocationInformation {
        ...BackwardsCompatibleLocationInformationFragment
      }
      ...ViolationTypeHistoryFragmentNoUserData
    }
  }
`

const CitizensPaymentsOrganizationFragment = gql`
  fragment CitizensPaymentsOrganizationFragment on Organization {
    ...OrganizationFragmentNoUsers
    canPayMultipleRecordTaskPayments
    isACHEnabled
    paymentFeeBps
    finixMerchants {
      id
      finixMerchantId
    }
    finixTransactionLimits {
      finixFormType
      maximumInvoiceAmountCents
    }
  }
`

const OrganizationFragmentNoUsers = gql`
  fragment OrganizationFragmentNoUsers on Organization {
    additionalMailerContent
    addressEntryType
    areAddressesAndParcelsPagesEnabled
    canSearchEntities
    city
    contactInfo
    department
    displayName
    gisSetup
    hasCodeEnforcementModule
    id
    inspectionSchedulingType
    isAIChatEnabled
    isCSLBEnabled
    isPublicGISSearchEnabled
    logoUrl
    showCodeEnforcementModule
    slug
    state
    shouldExposeInspectionAvailabilityInformationToPublic
    supportsMultipleViolationTypes
    timeZone
    uuid
  }
`

const OrganizationFragmentWithUsers = gql`
  fragment OrganizationFragmentWithUsers on Organization {
    ...OrganizationFragmentNoUsers
    users {
      ...UserDataFragment
    }
  }
`

const InsightsInspectionsOrganizationFragment = gql`
  fragment InsightsInspectionsOrganizationFragment on Organization {
    ...OrganizationFragmentNoUsers
    hasRecordTypeAllowingInspectionRequests
  }
`

const OrganizationByPathFragment = gql`
  fragment OrganizationByPathFragment on Organization {
    ...OrganizationFragmentNoUsers
    users {
      ...UserDataFragment
    }
    shouldExposeViolations
    homePageHeaderText
    homePageSubText
    homePageBackgroundImageUrl(viewportWidth: $viewportWidth)
    homePageBackgroundFile {
      ...FileFragment
    }
    publishedViewDashboards {
      id
      recordType {
        id
        displayName
      }
    }
  }
`

const RecordFragmentOverview = gql`
  fragment RecordFragmentOverview on Record {
    id
    createdAt
    currentUserHasAccess
    submittedAt
    expirationDate
    formattedName
    tags
    identifier
    issuedIdentifier
    status {
      id
      type
      name
    }
    applicantFormatted
    isDraft
    isTest
    uuid
    recordCollections {
      id
    }
    closedAt
    canceledAt
    currentRecordTaskGroup {
      type
      recordTaskTemplateGroup {
        taskTemplate {
          identifier
          id
        }
      }
    }
    mainBackwardsCompatibleLocationInformation {
      ...BackwardsCompatibleLocationInformationFragment
    }
    applicantCompanyOrRequestedToJoinCompany {
      ...CompanyFragment
    }
    applicantUser {
      ...UserDataFragment
    }
    ownerUser {
      ...UserDataFragment
    }
    createdByUser {
      ...UserDataFragment
    }
    recordTemplate {
      id
      applicantType
      name
      recordTypeId
      recordType {
        id
        name
        recordName
        isContact
        category
        allowInspections
        allowFees
      }
      recordSettingsDerived {
        ...RecordSettingsFragment
      }
    }
    organization {
      id
      entityLayers {
        id
      }
      slug
    }
    resolvedAt
    currentCaseHistory {
      id
      violationType {
        id
        name
      }
    }
  }
`

const RecordFragmentOverviewForApplicant = gql`
  fragment RecordFragmentOverviewForApplicant on Record {
    ...RecordFragmentOverview
    ownerUser {
      organization {
        slug
        state
        city
        displayName
      }
    }
  }
`

// This fragment relies on foreign key properties since it is also used to populate the CreatePlanReviewCommentInput
const PlanReviewCommentFragment = gql`
  fragment PlanReviewCommentFragment on PlanReviewComment {
    id
    comment
    createdFromAutoCheckFeedbackItemId
    createdFromAutoCheckFeedbackItem {
      id
    }
    isGeneralComment
    isRespondedToByCitizen
    isRevised
    xCoordinate
    yCoordinate
    filePageId
    filePage {
      id
      fileId
    }
    createdAt
    updatedAt
    status
    createdByUser {
      ...UserDataFragment
    }
    reviewId
    review {
      id
      recordPlansSet {
        id
        versionNumber
      }
    }
    childrenComments {
      id
      comment
      isGeneralComment
      isRevised
      createdAt
      updatedAt
      createdByUser {
        ...UserDataFragment
      }
    }
  }
`

const PlanReviewStampFragment = gql`
  fragment PlanReviewStampFragment on PlanReviewStamp {
    id
    uuid
    filePageId

    borderWidth
    color
    createdAt
    heightInches
    opacity
    rotation
    scale
    showBorder
    stampedAt
    widthInches
    x
    y

    createdByUser {
      ...UserDataFragment
    }
    filePage {
      id
      fileId
    }
    items {
      ...PlanReviewStampItemFragment
    }
    stampTemplate {
      ...StampTemplateFragment
    }
  }
  fragment PlanReviewStampItemFragment on PlanReviewStampItem {
    id

    fontSize
    heightInches
    isBolded
    isItalicized
    isUnderlined
    opacity
    rotation
    text
    textAlignment
    type
    widthInches
    xInches
    yInches

    imageFile {
      ...FileFragment
    }
  }
`

const PlanReviewLineArrowFragment = gql`
  fragment PlanReviewLineArrowFragment on PlanReviewLineArrow {
    id
    uuid
    filePageId

    color
    endX
    endY
    opacity
    rotation
    startX
    startY
    thicknessPx
    type
  }
`

const PlanReviewCircleFragment = gql`
  fragment PlanReviewCircleFragment on PlanReviewCircle {
    id
    uuid
    filePageId

    color
    endX
    endY
    opacity
    rotation
    startX
    startY
    thicknessPx
    type
  }
`

const PlanReviewQuadrilateralFragment = gql`
  fragment PlanReviewQuadrilateralFragment on PlanReviewQuadrilateral {
    id
    uuid
    filePageId

    color
    endX
    endY
    opacity
    rotation
    startX
    startY
    thicknessPx
  }
`

const PlanReviewTextAnnotationFragment = gql`
  fragment PlanReviewTextAnnotationFragment on PlanReviewTextAnnotation {
    id
    uuid
    filePageId
    reviewId

    opacity
    rotation
    textHtml
    x
    y

    createdAt
    updatedAt

    codeBookItems {
      ...CodeBookItemFragment
    }
    createdByUser {
      ...UserDataFragment
    }
  }
`

const EmailTemplateFragment = gql`
  fragment EmailTemplateFragment on EmailTemplate {
    id
    name
    subject
    content
    signature
    type
    createdByUser {
      ...UserDataFragment
    }
    archivedAt
    archivedByUser {
      ...UserDataFragment
    }
  }
`

const DocumentTemplateFragment = gql`
  fragment DocumentTemplateFragment on DocumentTemplate {
    id
    name
  }
`

const FeeDropdownOptionFragment = gql`
  fragment FeeDropdownOptionFragment on FeeDropdownOption {
    inputString
    outputCents
  }
`
const FeeVersionFragment = gql`
  fragment FeeVersionFragment on FeeVersion {
    id
    type
    amount
    amountExact
    minimumAmount
    maximumAmount
    calculatedOn
    valuationMultiplier
    fee {
      id
      name
      description
    }
    generalLedgerNumber {
      ...GeneralLedgerNumberFragment
    }
    dropdownOptions {
      ...FeeDropdownOptionFragment
    }
  }
`

const FeeFragment = gql`
  fragment FeeFragment on Fee {
    id
    isArchived
    name
    description
    latestVersion {
      id
      type
      amount
      amountExact
      minimumAmount
      maximumAmount
      calculatedOn
      valuationMultiplier
      generalLedgerNumber {
        ...GeneralLedgerNumberFragment
      }
      dropdownOptions {
        ...FeeDropdownOptionFragment
      }
    }
  }
`

const PaymentTransferFragment = gql`
  fragment PaymentTransferFragment on PaymentTransfer {
    id
    createdAt
    totalAmountCents
    serviceFeeCents
    revenueCents
    failureCode
    failureMessage
    state
    type
    paymentMethod
    finixMerchantId
    checkNumber
    notes
  }
`

const RecordTaskFragment = gql`
  fragment RecordTaskFragment on RecordTask {
    id
    dueDate
    completedAt
    name
    type
    ownerUser {
      ...UserDataFragment
    }
    status
    recordTaskGroup {
      record {
        uuid
        identifier
        issuedIdentifier
        mainBackwardsCompatibleLocationInformation {
          ...BackwardsCompatibleLocationInformationFragment
        }
        recordTemplate {
          recordTypeId
          name
        }
      }
    }
    recordTaskTemplate {
      taskTemplate {
        identifier
      }
    }
  }
`

const InspectionTemplateFragment = gql`
  fragment InspectionTemplateFragment on InspectionTemplate {
    id
    archivedAt
    defaultDurationMinutes
    name
    createdAt
    fieldGroup {
      ...FieldGroupFragment
    }
    createdByUser {
      ...UserDataFragment
    }
  }
`

const InspectionResultFragment = gql`
  fragment InspectionResultFragment on RecordTaskInspectionResult {
    id
    name
    isPassing
    isActive
    createdAt
    createdByUser {
      ...UserDataFragment
    }
    recordType {
      id
      displayName
    }
  }
`

const RecordTaskInspectionAttemptLogEntryFragment = gql`
  fragment RecordTaskInspectionAttemptLogEntryFragment on RecordTaskInspectionAttemptLogEntry {
    id
    inspectionStartedAt
    inspectionEndedAt
    isAmendment
    isDraft
    loggedAt
    fieldGroup {
      id
      fields {
        ...FieldFragment
      }
    }
    inspectorUser {
      ...UserDataFragment
    }
    reportFile {
      ...FileFragment
    }
    result {
      ...RecordTaskInspectionResultFragment
    }
    loggedByUser {
      ...UserDataFragment
    }
  }
`

const RecordTaskInspectionFragment = gql`
  fragment RecordTaskInspectionFragment on RecordTaskInspection {
    id
    order
    attempts {
      ...RecordTaskInspectionAttemptDetailsFragment
    }
    inspectionTemplate {
      name
    }
    visibleToApplicant
  }
`

const RecordTaskInspectionGroupFragment = gql`
  fragment RecordTaskInspectionGroupFragment on RecordTaskInspectionGroup {
    id
    name
    order
    alwaysAvailableToApplicants
    inspections {
      ...RecordTaskInspectionFragment
    }
  }
`

const RecordTypeFragment = gql`
  fragment RecordTypeFragment on RecordType {
    id
    addressEntryType
    allowFees
    allowInspections
    allowPlanReviews
    applyDescription
    areMeetingsEnabled
    category
    displayName
    icon
    isContact
    issuedIdentifierAlgorithm
    name
    recordName
    shouldEmailCitizens
    shouldEmailStaff
    shouldShowInStaffNavigation
    visibleOnOrganizationHomepage
    recordSettings {
      ...RecordSettingsFragment
    }
    recordSettingsDerived {
      ...RecordSettingsFragment
    }
  }
`

const CompanyFragment = gql`
  fragment CompanyFragment on Company {
    id
    uuid
    name
    phoneNumber
    emailAddress
    addressLine1
    addressLine2
    zip
    city
    state
    myRole
  }
`

const DocumentTemplateSectionParamsFragment = gql`
  fragment DocumentTemplateSectionParamsFragment on DocumentTemplateSection {
    id
    type
    order
    title
    field {
      ...FieldFragment
    }
    shouldRenderBlankSignature
  }
`

const DocumentTemplateItemParamsFragment = gql`
  fragment DocumentTemplateItemParamsFragment on DocumentTemplateItem {
    id
    params {
      letterhead {
        left
        right
        centerText
        centerFileId
      }
      headerText {
        size
        bold
        position
        text
        underline
      }
      applicantInfo {
        showApplicantInfo
        applicantHeader
        showCompanyInfo
        companyHeader
      }
      customField {
        renderAddressAsPropertyOwnerMailingAddress
        renderLabel
        position
      }
      documentExpirationDate {
        position
      }
      documentIssuedDate {
        position
      }
      recordTemplateField {
        renderAddressAsPropertyOwnerMailingAddress
        renderLabel
        position
      }
      gap {
        sizePx
      }
      permitType {
        position
      }
      parcelNumber {
        position
      }
      inspectionsList {
        type
      }
      documentNumber {
        position
        prefix
      }
      signature {
        shouldRenderBlankSignature
      }
    }
    file {
      ...FileFragment
    }
    field {
      ...FieldFragment
    }
    imageFile {
      ...FileFragment
    }
  }
`

const GenerateDocumentTemplateFragment = gql`
  fragment GenerateDocumentTemplateFragment on DocumentTemplate {
    id
    name
    showGISFields
    customFieldItems {
      ...DocumentTemplateItemParamsFragment
    }
    recordTemplateFieldItems {
      ...DocumentTemplateItemParamsFragment
    }
    signatures {
      labelFallback
      field {
        ...FieldFragment
      }
      documentTemplateSection {
        ...DocumentTemplateSectionParamsFragment
      }
      documentTemplateItem {
        ...DocumentTemplateItemParamsFragment
      }
    }
  }
`

const DocumentTemplateFieldFragment = gql`
  fragment DocumentTemplateFieldFragment on DocumentTemplateField {
    labelFallback
    field {
      ...FieldFragment
    }
    documentTemplateSection {
      ...DocumentTemplateSectionParamsFragment
    }
    documentTemplateItem {
      ...DocumentTemplateItemParamsFragment
    }
  }
`

const ApplyFormRecordTemplateFragment = gql`
  fragment ApplyFormRecordTemplateFragment on RecordTemplate {
    id
    uuid
    applicantType
    icon
    name
    tagline
    submissionPageText
    whenRequired
    rejectionReasons
    applicationGroup {
      id
      name
    }
    feeInfo {
      ...PublicFacingRecordTemplateFeeInfoFragment
    }
    applyFormFileFields {
      ...FieldTemplateDetailedFragment
    }
    applyFormInspectionTemplates {
      name
    }
    recordTemplateFieldGroups {
      ...RecordTemplateFieldGroupDetailedFragment
    }
    recordSettingsDerived {
      ...RecordSettingsFragment
    }
    recordType {
      ...RecordTypeApplyFragment
    }
  }
  fragment PublicFacingRecordTemplateFeeInfoFragment on PublicFacingRecordTemplateFeeInfo {
    name
    explanation
    dropdownOptions {
      ...FeeDropdownOptionFragment
    }
  }
`
const RecordTypePickFragment = gql`
  fragment RecordTypePickFragment on RecordType {
    id
    applyDescription
    category
    displayName
    icon
    name
    recordName
  }
`
const RecordTypeApplyFragment = gql`
  fragment RecordTypeApplyFragment on RecordType {
    ...RecordTypePickFragment
    addressEntryType
    organization {
      id
      uuid
      slug
      city
      state
      displayName
    }
  }
`

const RecordTaskPaymentFragment = gql`
  fragment RecordTaskPaymentFragment on RecordTaskPayment {
    id
    invoiceNumber
    receiptNumber
    status
    totalWhenInvoiceRequestedCents
    shouldOrganizationAbsorbServiceFee
    isPayable
    paidAt
    requestedAt
    voidedAt
    dueDate
    paidByUser {
      ...UserDataFragment
    }
    voidedByUser {
      ...UserDataFragment
    }
    organizationFinixMerchant {
      id
      description
      finixMerchantId
    }
    record {
      applicantUser {
        ...UserDataFragment
      }
      identifier
      issuedIdentifier
      uuid
      recordTemplate {
        name
        recordType {
          name
          recordName
        }
      }
      organization {
        id
        displayName
        slug
        paymentProvider
        isACHEnabled
        paymentFeeBps
      }
    }
    invoiceFile {
      ...FileFragment
    }
    receiptFile {
      ...FileFragment
    }
    lineItemRecordTaskFees {
      ...RecordTaskFeeDetailedFragment
    }
    consolidatedSurchargesTotal
    inProgressTotalCents
  }
`

const ValuationVersionFragment = gql`
  fragment ValuationVersionFragment on ValuationVersion {
    id
    group
    occupancyType
    constructionType
    amountCents
  }
`

const RecordTaskFeeValuationFragment = gql`
  fragment RecordTaskFeeValuationFragment on RecordTaskFeeValuation {
    id
    quantity
    valuationVersion {
      ...ValuationVersionFragment
    }
  }
`
const RecordTaskFeeDetailedFragment = gql`
  fragment RecordTaskFeeDetailedFragment on RecordTaskFee {
    id
    inputValue {
      ...RecordTaskFeeInputValueFragment
    }
    isParent
    childFeesTotal
    description
    calculationExplanation
    feeVersion {
      ...FeeVersionFragment
    }
    parentRecordTaskFees {
      id
      total
      feeVersion {
        fee {
          name
        }
      }
    }
    total
    valuations {
      ...RecordTaskFeeValuationFragment
    }
    wasAddedViaAIAutomation
  }
`
const RecordComponentPaymentTransferFragment = gql`
  fragment RecordComponentPaymentTransferFragment on PaymentTransfer {
    id
    createdAt
    paymentMethod
    revenueCents
    serviceFeeCents
    state
    totalAmountCents
    type
    notes
  }
`
const RecordComponentRecordTaskPaymentFragment = gql`
  fragment RecordComponentRecordTaskPaymentFragment on RecordTaskPayment {
    id
    dueDate
    isPayable
    isUndoable
    status
    paidAt
    paymentMethodsLabel
    requestedAt
    voidedAt
    recordTaskId
    totalWhenInvoiceRequestedCents
    invoiceNumber
    receiptNumber
    shouldOrganizationAbsorbServiceFee
    feeSchedule {
      id
    }
    invoiceFile {
      ...FileFragment
    }
    receiptFile {
      ...FileFragment
    }
    requestedByUser {
      ...UserDataFragment
    }
    paidByUser {
      ...UserDataFragment
    }
    voidedByUser {
      ...UserDataFragment
    }
    organizationFinixMerchant {
      id
      description
      finixMerchantId
    }
    feeSchedule {
      id
      name
    }
    recordTask {
      id
      deletedAt
      name
      status
      recordTaskGroup {
        id
        name
        workflow {
          id
          completedAt
          createdAt
          status
          workflowTemplate {
            name
          }
        }
      }
    }
    lineItemRecordTaskFees {
      ...RecordTaskFeeDetailedFragment
    }
    consolidatedSurcharges {
      ...RecordTaskFeeDetailedFragment
    }
    consolidatedSurchargesTotal
    inProgressTotalCents
    paymentTransfers {
      ...RecordComponentPaymentTransferFragment
    }
  }
`

const RecordTaskFeeFragment = gql`
  fragment RecordTaskFeeFragment on RecordTaskFee {
    id
    fee {
      id
      name
      latestVersion {
        id
        generalLedgerNumber {
          glNumber
        }
      }
    }
    processedByUser {
      ...UserDataFragment
    }
  }
`

const RecordTaskPlanReviewOverviewFragment = gql`
  fragment RecordTaskPlanReviewOverviewFragment on RecordTask {
    id
    dueDate
    name
    type
    ownerUser {
      ...UserDataFragment
    }
    status
    plansSetReview {
      id
      recordPlansSet {
        id
        versionNumber
      }
    }
    recordTaskGroup {
      id
      record {
        id
        identifier
        issuedIdentifier
        uuid
        mainBackwardsCompatibleLocationInformation {
          ...BackwardsCompatibleLocationInformationFragment
        }
        recordTemplate {
          recordTypeId
          name
        }
      }
    }
  }
`

const PlanReviewRecordFragment = gql`
  fragment PlanReviewRecordFragment on Record {
    id
    identifier
    issuedIdentifier
    uuid
    recordTemplate {
      id
      recordType {
        id
        name
        recordName
        addressEntryType
      }
    }
    mostRecentPlansSet {
      id
      versionNumber
    }
    plansSets {
      id
      isRevision
      versionDisplayName
      versionNumber
    }
    currentFormFieldGroupVersion {
      id
      fieldGroups {
        ...FieldGroupFragment
      }
    }
    allPlanReviewComments {
      ...PlanReviewCommentFragment
    }
    allPlanReviewCodeBookItems {
      ...CodeBookItemOptionFragment
    }
  }
`

const OrganizationFinixMerchantsFragment = gql`
  fragment OrganizationFinixMerchantsFragment on OrganizationFinixMerchant {
    id
    finixMerchantId
    description
  }
`

const UserDataFragmentWithRecordTypeAccesses = gql`
  fragment UserDataFragmentWithRecordTypeAccesses on User {
    ...UserDataFragment
    recordTypeAccesses {
      id
      recordType {
        id
        name
      }
    }
  }
`

const LocationSnapshotInDepthFragment = gql`
  fragment LocationSnapshotInDepthFragment on LocationSnapshot {
    id
    locationObjectId
    addressDisplayName
    parcelDisplayName
    parcelCoordinates {
      ...CoordinateFragment
    }
    fields {
      key
      value
    }
  }
`

const AddressFieldFragment = gql`
  fragment AddressFieldFragment on Field {
    id
    label
    str
    locationSnapshot {
      ...LocationSnapshotInDepthFragment
    }
    backwardsCompatibleLocationInformation {
      ...BackwardsCompatibleLocationInformationFragment
    }
  }
`

const RecordTemplateScheduledEventFragment = gql`
  fragment RecordTemplateScheduledEventFragment on RecordTemplateScheduledEvent {
    id
    changeToStatus {
      ...RecordTypeStatusFragment
    }
    emailApplicant
    emailOwner
    emailTemplateId
    isExpiration
    type
    beginWorkflowTemplate {
      id
    }
    dateTemplate {
      ...DateTemplateFragment
    }
    recipients {
      userId
      emailAddress
    }
  }
`

const RecordTaskFeeInputValueFragment = gql`
  fragment RecordTaskFeeInputValueFragment on RecordTaskFeeInputValue {
    __typename
    ... on RecordTaskFeeInputQuantity {
      quantity
      isImmutable
      immutableReason
      invalidReason
    }
    ... on RecordTaskFeeInputText {
      text
    }
  }
`

const PaymentButtonRecordTaskPayment = gql`
  fragment PaymentButtonRecordTaskPayment on RecordTaskPayment {
    id
    consolidatedSurchargesTotal
    inProgressTotalCents
    invoiceNumber
    organizationFinixMerchant {
      id
      finixMerchantId
    }
    shouldOrganizationAbsorbServiceFee
    totalWhenInvoiceRequestedCents
    lineItemRecordTaskFees {
      ...RecordTaskFeeDetailedFragment
    }
    record {
      id
      displayIdentifier
    }
  }
`

const PlanReviewCoordinateFragment = gql`
  fragment PlanReviewCoordinateFragment on PlanReviewCoordinate {
    id
    x
    y
    order
  }
`

const PlanReviewMeasurementCalibrationFragment = gql`
  fragment PlanReviewMeasurementCalibrationFragment on PlanReviewMeasurementCalibration {
    id
    uuid

    filePageId
    pageHeightPx
    pageWidthPx
    recordPlansSetId
    reviewId
    rotation
    unit
    unitCount

    endCoordinate {
      ...PlanReviewCoordinateFragment
    }
    startCoordinate {
      ...PlanReviewCoordinateFragment
    }
  }
`

const PlanReviewMeasurementFragment = gql`
  fragment PlanReviewMeasurementFragment on PlanReviewMeasurement {
    id
    uuid

    area
    calibrationId
    filePageId
    measurementType
    reviewId
    rotation

    coordinates {
      ...PlanReviewCoordinateFragment
    }
    centroid {
      x
      y
    }
  }
`

const RecordTemplateFragmentForReporting = gql`
  fragment RecordTemplateFragmentForReporting on RecordTemplate {
    id
    name
    applicationFields {
      ...FieldFragmentForReporting
    }
  }
`

const FieldFragmentForReporting = gql`
  fragment FieldFragmentForReporting on Field {
    id
    isArchived
    label
    type
    showInGeneratedDocuments
    fieldGroupTemplate {
      id
      isSharedFieldGroup
      usedInRecordTemplates {
        id
        name
      }
    }
  }
`

const ReportFilterFragment = gql`
  fragment ReportFilterFragment on ReportFilter {
    id
    name
    description
    params
    type
    filterType
    reportDisplayType
    recordTypeId
    reportFields {
      ...FieldFragmentForReporting
    }
    recordTemplates {
      ...RecordTemplateFragmentForReporting
    }
  }
`

const WorkflowMostRecentPlansSetFragment = gql`
  fragment WorkflowMostRecentPlansSetFragment on RecordPlansSet {
    id

    allReviewsDoneOffline
    isRevision
    readyForApplicantUpdate
    versionNumber

    createdByUser {
      ...UserDataFragment
    }
    reviews {
      ...WorkflowPlansSetReviewFragment
    }
  }
  fragment WorkflowPlansSetReviewFragment on RecordPlansSetReview {
    id
    type
    totalComments
    totalCommentsRespondedByApplicant
    offlineFile {
      ...FileFragment
    }
    rejected
    recordPlansSetId
    recordTask {
      id
      dueDate
      ownerUser {
        ...UserDataFragment
      }
      status
    }
    result {
      id
      name
      isPassing
    }
    deleteReviewPlansSetDecision
  }
`

const PlanReviewMostRecentPlansSetReadyFragment = gql`
  fragment PlanReviewMostRecentPlansSetReadyFragment on Record {
    id
    mostRecentPlansSet {
      id
      readyForApplicantUpdate
    }
  }
`

const RecipientFragment = gql`
  fragment RecipientFragment on Recipient {
    id
    emailAddress
    basedOnField {
      ...RecipientContactFieldFragment
    }
    contact {
      ...ContactFragment
    }
    recordAccess {
      ...RecordAccessFragment
    }
    user {
      ...UserDataFragment
    }
  }
`

const RecordScheduledEventFragment = gql`
  fragment RecordScheduledEventFragment on RecordScheduledEvent {
    id
    date
    emailApplicant
    emailContent
    emailOwner
    emailSignature
    emailSubject
    isDateDerivedFromExpiration
    isExpiration
    isExpirationNotification
    type
    wasCreatedByAutomation
    addRecordTaskTemplateGroup {
      id
      identifier
      type
    }
    beginWorkflowTemplate {
      id
      name
    }
    changeToStatus {
      ...RecordTypeStatusFragment
    }
    emailFiles {
      ...FileFragment
    }
    emailTemplate {
      ...EmailTemplateFragment
    }
    recipients {
      ...RecipientFragment
    }
    willBeCancelledOnCompletionOfRecordTask {
      name
    }
  }
`

const RecordScheduledEventStatusOptionFragment = gql`
  fragment RecordScheduledEventStatusOptionFragment on RecordTypeStatus {
    id
    type
    name
    recordTypeId
  }
`

const DateTemplateFragment = gql`
  fragment DateTemplateFragment on DateTemplate {
    type
    date {
      day
      month
      minimumDelayDays
    }
    interval {
      count
      relativeToDate
      temporalPosition
      type
    }
  }
`

const RecordTypeStatusFragment = gql`
  fragment RecordTypeStatusFragment on RecordTypeStatus {
    id
    name
    type
    isResolved
  }
`

const InspectionTemplatesCellInspectionTemplateFragment = gql`
  fragment InspectionTemplatesCellInspectionTemplateFragment on InspectionTemplate {
    ...InspectionTemplateFragment
    inputsCount
  }
`

const CreateViolationFragment = gql`
  fragment CreateViolationFragment on Violation {
    id
    identifier
  }
`

const CoordinateFragment = gql`
  fragment CoordinateFragment on Coordinate {
    latitude
    longitude
  }
`

const AITrainingMaterialFragment = gql`
  fragment AITrainingMaterialFragment on AITrainingMaterial {
    id
    type
    text
    processingStatus
    processingError
    createdAt
    citationCount
    file {
      ...FileFragment
    }
  }
`

const ActionProposalFragment = gql`
  fragment ActionProposalFragment on ActionProposal {
    id
    createdAt
    highRiskActionProposalReason
    isHighRiskProposal
    isPending
    isUnconfirmedHighRiskProposal
    summary
    createdByUser {
      ...UserDataFragment
    }
  }
`

const ActionTemplateFragment = gql`
  fragment ActionTemplateFragment on ActionTemplate {
    type
    displayName
    targetEntity
    isHighRiskAction
  }
`

const AIConfigChatFragment = gql`
  fragment AIConfigChatFragment on AIConfigChat {
    id
    responseType
    aiChat {
      ...AIChatBaseFragment
    }
    actionProposals {
      ...ActionProposalFragment
    }
  }
`

const AIConfigChatSessionFragment = gql`
  fragment AIConfigChatSessionFragment on AIConfigChatSession {
    id
    aiConfigChats {
      ...AIConfigChatFragment
    }
  }
`

const EntityFieldFragment = gql`
  fragment EntityFieldFragment on EntityVersionField {
    id
    entityFieldTemplate {
      id
      label
      isPropertyOwner
      isPropertyOwnerAddress
      isAddressDisplayName
      isParcelDisplayName
    }
    stringifiedValue
  }
`

const RecordHistoryItemFragment = gql`
  fragment RecordHistoryItemFragment on RecordHistoryItem {
    user {
      id
      title
      firstName
      lastName
      type
      imageUrl
    }
    text
    time
    type
    isAutomated
    recordEmailId
    recordEmailIsTest
    recordEmailRecipients
    recordEmailSubject
    recordEmailHasHtml
    emailClickCount
    previousRecordStatusName
    previousRecordStatusType
    newRecordStatusName
    newRecordStatusType
    recordComment
    recordCommentId
    workflowType
    meetingUuid
    meetingDescription
    nearbyPropertiesReportFileName
    nearbyPropertiesReportFileDownloadUrl
    autoCheckExecutionId
  }
`

const AlertFragment = gql`
  fragment AlertFragment on Alert {
    id
    uuid
    alertSource
    text
    isAutomated
    createdAt
    updatedAt
    alertSourceDisplay {
      displayName
      group
    }
    entity {
      id
    }
    createdByUser {
      ...UserNameFragment
    }
    record {
      id
    }
    updatedByUser {
      ...UserNameFragment
    }
  }
`

const AlertSourceCandidateFragment = gql`
  fragment AlertSourceCandidateFragment on AlertSourceCandidate {
    alertSource
    alertSourceDisplay {
      displayName
      group
    }
    entityId
    recordId
  }
`

const NotificationSettingsFragment = gql`
  fragment NotificationSettingsFragment on NotificationSettings {
    id
    areNonEssentialEmailNotificationsDisabledForGovStaff
  }
`

// order matters - some fragments reference other ones!
fragmentRegistry.register(
  AddressFragment,
  FileFragment,
  ThumbnailFileFragment,
  FilePageFragment,
  FileUploadCredentialsFragment,
  EntityVersionFragment,
  RecordTaskInspectionResultFragment,
  FieldValueBaseFragment,
  FieldFragment,
  PlanReviewFileFragment,
  PlanReviewFieldGroupFragment,
  UserDataFragmentWithCompany,
  FieldGroupFragment,
  TaskTemplatesFragment,
  ViolationFieldsFragment,
  ViolationTypeHistoryFragment,
  ViolationTypeHistoryFragmentNoUserData,
  ContactFragment,
  ViolationInspectionsFragment,
  AuthorizedExternalCalendarFragment,
  ExternalCalendarFragment,
  UserDataFragment,
  CitizensPaymentsOrganizationFragment,
  OrganizationFragmentNoUsers,
  OrganizationFragmentWithUsers,
  OrganizationByPathFragment,
  OrganizationHomePageFragment,
  RecordFragmentOverview,
  RecordFragmentOverviewForApplicant,
  PlanReviewCommentFragment,
  PlanReviewStampFragment,
  EmailTemplateFragment,
  DocumentTemplateFragment,
  GeneralLedgerNumberFragment,
  FeeDropdownOptionFragment,
  FeeVersionFragment,
  FeeFragment,
  FeeScheduleFragment,
  PaymentTransferFragment,
  SettlementFragmentOverview,
  RecordTaskFragment,
  InspectionTemplateFragment,
  InspectionResultFragment,
  RecordTaskInspectionFragment,
  RecordTaskInspectionGroupFragment,
  RecordTypeFragment,
  RecordTypeApplicationGroupFragment,
  CompanyFragment,
  DocumentTemplateItemParamsFragment,
  DocumentTemplateSectionParamsFragment,
  RecordTemplatePickFragment,
  ApplyFormRecordTemplateFragment,
  RecordTypePickFragment,
  FieldGroupFragmentWithBasedOnFieldIds,
  RecordTaskPaymentFragment,
  RecordTaskFeeFragment,
  GenerateDocumentTemplateFragment,
  DocumentTemplateFieldFragment,
  RecordTaskInspectionAttemptLogEntryFragment,
  RecordTaskInspectionAttemptFragment,
  RecordTaskInspectionAttemptDetailsFragment,
  RecordTaskInspectionAttemptOverviewFragment,
  RecordTaskPlanReviewOverviewFragment,
  ValuationVersionFragment,
  RecordTaskFeeValuationFragment,
  RecordComponentRecordTaskPaymentFragment,
  RecordTaskFeeDetailedFragment,
  RecordComponentPaymentTransferFragment,
  OrganizationFinixMerchantsFragment,
  UserDataFragmentWithRecordTypeAccesses,
  CoordinateFragment,
  LocationSnapshotInDepthFragment,
  AddressFieldFragment,
  RecordTemplateScheduledEventFragment,
  RecordTypeApplyFragment,
  PlanReviewLineArrowFragment,
  PlanReviewCircleFragment,
  PlanReviewQuadrilateralFragment,
  PlanReviewTextAnnotationFragment,
  UserNameFragment,
  RecordTaskFeeInputValueFragment,
  PaymentButtonRecordTaskPayment,
  PlanReviewCoordinateFragment,
  PlanReviewMeasurementCalibrationFragment,
  PlanReviewMeasurementFragment,
  RecordTemplateFragmentForReporting,
  ReportFilterFragment,
  WorkflowMostRecentPlansSetFragment,
  PlanReviewMostRecentPlansSetReadyFragment,
  RecordScheduledEventFragment,
  RecordScheduledEventStatusOptionFragment,
  DateTemplateFragment,
  RecordTypeStatusFragment,
  SentEmailFragment,
  SentEmailRecipientFragment,
  SentEmailDetailedFragment,
  RecordEmailFragment,
  RecordEmailFileFragment,
  InspectionTemplatesCellInspectionTemplateFragment,
  RecipientContactFieldFragment,
  RecipientFragment,
  CreateViolationFragment,
  EnumerationFragment,
  FormulaFragment,
  FormulaOptionFragment,
  WorkflowTemplateOptionFragment,
  RecordAccessFragment,
  FileOnRecordDeprecatedFragment,
  RecordTemplateOptionFragment,
  RecordTaskTemplateGroupRequestedRecordFragment,
  WorkflowOptionFragment,
  RecordOptionFragment,
  RelatedRecordFragment,
  RecordOnMeetingFragment,
  RecordFragment,
  RecordIdentifierFragment,
  RecordIssuedIdentifierFragment,
  ViolationFragment,
  ViolationTypeFragment,
  ViolationCodeFragment,
  ViolationMailerTitleFragment,
  ViolationInspectionFragment,
  ViolationMailerFragment,
  ViolationFileFragment,
  ViolationCommentFragment,
  RecordTaskTemplateGroupOptionFragment,
  LateFeeConfigurationFragment,
  RecordSettingsFragment,
  OverridableRecordSettingsFragment,
  OverridableRecordSettingSourcesFragment,
  RecordSettingSourceFragment,
  CodeBookItemFragment,
  CodeBookItemOptionFragment,
  CodeBookFragment,
  PlanReviewRecordFragment,
  RecordTaskChecklistItemFragment,
  RecordTaskBaseFragment,
  WorkflowRecordTaskFragment,
  RecordTaskGroupBaseFragment,
  WorkflowRecordTaskGroupFragment,
  WorkflowFragment,
  GeneratedFileFragment,
  AutomationTemplateFragment,
  LetterheadDetailFragment,
  MeetingOptionFragment,
  CalendarEventFragment,
  RecordPlansSetReviewResultConfigFragment,
  AIChatSessionFragment,
  StaffAIChatSessionPreviewFragment,
  StaffAIChatSessionWithMessagesFragment,
  BackwardsCompatibleAddressFragment,
  BackwardsCompatibleLocationFieldFragment,
  EntityLayerFragment,
  FieldGroupOnRecordSettingsFragment,
  FieldGroupTableFragment,
  FieldFragmentForReporting,
  FieldTemplateListFragment,
  FieldTemplateDetailedFragment,
  FieldGroupTemplateDetailedFragment,
  RecordTemplateFieldGroupDetailedFragment,
  RecordTemplateFieldGroupFragment,
  UserRequestToJoinCompanyFragment,
  FieldValueDetailedFragment,
  FieldGroupValueDetailedFragment,
  UserOnCompanyFragment,
  UserCompanyInvitationFragment,
  SearchEntityVersionFragment,
  FieldDetailedFragment,
  FieldGroupDetailedFragment,
  ConfigConnectionFragment,
  ViewDataFragment,
  ViewDataElementFragment,
  ViewFragment,
  ViewColumnFragment,
  ViewFilterFragment,
  ViewFilterDateFragment,
  ViewFilterNumberFragment,
  ViewFilterMembershipFragment,
  ViewSortFragment,
  ViewColumnTemplateFragment,
  ViewColumnUsedInViewFragment,
  ViewFilterOptionFragment,
  FieldTemplateForViewFragment,
  WidgetDataBucketFragment,
  WidgetDataGroupFragment,
  ValueForFunctionFragment,
  WidgetDataGroupByFragment,
  AITrainingMaterialFragment,
  AIChatCitationFragment,
  FillRecordTemplateApplyFormFieldFragment,
  ActionProposalFragment,
  AIConfigChatFragment,
  AIChatBaseFragment,
  AIAssistantChatFragment,
  AIAssistantChatAssociatedAIChatbotFAQFragment,
  AIChatResponseEventFragment,
  AIConfigChatSessionFragment,
  AIChatFallbackContactFragment,
  EntityForViewFragment,
  ViewDashboardOverviewFragment,
  ViewDashboardFragment,
  EntityFieldFragment,
  ActionTemplateFragment,
  MeetingResultFragment,
  InsightsInspectionsOrganizationFragment,
  StampTemplateFragment,
  ScrapeTemplateFragment,
  ScrapeFragment,
  RecordHistoryItemFragment,
  RecordUpdatedRecordTemplateHistoriesFragment,
  SearchEntityVersionsByLocationFragment,
  EntityVersionWithFieldsFragment,
  AlertFragment,
  AlertSourceCandidateFragment,
  AlertsForRecordFragment,
  OrganizationHolidayDetailFragment,
  InspectionAvailabilityWindowDataFragment,
  PersonalInspectionAvailabilityWindowDataFragment,
  NotificationSettingsFragment,
  SettlementPaymentTransferRecordTaskFeeFragment,
  SettlementPaymentTransferFragment,
  SettlementFragment,
  InspectionAvailabilitySlotFragment,
  InspectionAvailabilityDayFragment,
  AutoCheckFeedbackItemSourceFragment,
  AutoCheckFeedbackItemBaseFragment,
  AutoCheckFeedbackItemFragment,
  AutoCheckExecutionFragment,
  AutoCheckFeedbackUpdateFragment
)
